import { useEffect, useRef, useState } from 'react'
import { AuthScreen, useAuthScreenProps, useSignOut } from './components/auth'
import {
  ConfirmPopover,
  Drawer,
  InstallPromptSheet,
  LanguagePopover,
  NudgeSheet,
  PhoneFrame,
  RoutePlaceholder,
} from './components'
import { BootSplash, useBootSplashVisible } from './components/BootSplash'
import { setShellScroller } from './components/shellScroller'
// A direct import rather than one through `components/index.ts`: the boundary is the shell's own
// backstop, not part of the shared library screens compose from.
import { ErrorBoundary } from './components/ErrorBoundary'
import { DeleteAccountDialog } from './account/DeleteAccountDialog'
import { eraseMyAccount } from './account/api'
import { useSession } from './auth/context'
import { NetworkError } from './auth/transport'
import { useLocale, useTranslation } from './i18n/I18nProvider'
import type { Locale } from './i18n/locales'
import { patchProfile } from './screens/shared/api'
import { useInstallPrompt } from './hooks/useInstallPrompt'
import { isNativeContainer } from './pwa/nativeContainer'
import { useAppUpdate } from './pwa/useAppUpdate'
import { isBetaOfferPlatform } from './pwa/betaOffer'
import { androidBetaUrl, iosBetaUrl } from './pwa/websiteLinks'
import { useAuthStatus, useDismissOnBack, useNavigation } from './router'
import { setCohortIdentity, useBuildBelowPromptFloor, useFlag } from './config'
import { AboutScreen } from './screens/about/AboutScreen'
import { AccessScreen } from './screens/access/AccessScreen'
import { ActivityScreen } from './screens/activity/ActivityScreen'
import { CopyMembersScreen } from './screens/copyMembers/CopyMembersScreen'
import { DevicesScreen } from './screens/devices/DevicesScreen'
import { GateDetailScreen } from './screens/gate/GateDetailScreen'
import { HomeScreen } from './screens/home/HomeScreen'
import { InstallScreen } from './screens/install/InstallScreen'
import { InviteScreen } from './screens/invite/InviteScreen'
import { PeopleScreen } from './screens/people/PeopleScreen'
import { PersonalizeScreen } from './screens/personalize/PersonalizeScreen'
import { PreferencesScreen } from './screens/preferences/PreferencesScreen'
import { ShabbatScreen } from './screens/shabbat/ShabbatScreen'

/**
 * The app frame every route renders into (PRD §1). It owns navigation chrome (app bar,
 * drawer, nudge, install sheet) and the auth/loading boot gate; it renders no gate content
 * of its own — that is P7/P9/P15's, one `RoutePlaceholder` at a time until each PRD lands.
 */
export function App(): JSX.Element {
  const { route, navigate } = useNavigation()
  const auth = useAuthStatus()
  const shabbatPanel = useFlag('shabbat_panel')
  const { t: tAccount } = useTranslation('account')
  const { t: tAndroidBeta } = useTranslation('androidBeta')
  const { t: tCommon } = useTranslation('common')
  const { t: tDrawer } = useTranslation('drawer')
  const { locale, setLocale } = useLocale()
  const signOut = useSignOut()
  const session = useSession()
  const authScreenProps = useAuthScreenProps()
  const { platform, canInstall, canPromptNatively, promptInstall } = useInstallPrompt()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [iosSheetOpen, setIosSheetOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [deleteAccountInFlight, setDeleteAccountInFlight] = useState(false)
  const [deleteAccountError, setDeleteAccountError] = useState<'server' | 'network' | 'mismatch' | undefined>(
    undefined,
  )
  /**
   * **The erasure's in-flight state is a ref AND a state, and they answer different questions.**
   *
   * `deleteAccountInFlight` is what the dialog renders from — the backdrop it refuses, the
   * disabled Cancel, and `useDismissOnBack`'s `dismissible` — so it has to be state. But a state
   * read inside `confirmDeleteAccount` is the value as of the last render, which is one tick too
   * old to stop a second dispatch. `deleteAccountRequestRef` is the synchronous half, set before
   * the `await` and cleared with it.
   *
   * It is needed **because** `SlideToConfirm` now releases its own commit guard when `busy` falls
   * (that component's docblock): before that, one mounted slider could dispatch exactly once and
   * the question could not arise. This is the guard that replaces the one the fix spends.
   *
   * `deleteAccountAttemptRef` numbers the attempts, so a result that arrives after its dialog has
   * been superseded writes nothing. **No shipped path produces two overlapping attempts** — the
   * ref above is what makes that true — so what this defends is the next relaxation of the busy
   * lock, and `openDeleteAccount()` below is where an attempt is actually invalidated.
   */
  const deleteAccountRequestRef = useRef(false)
  const deleteAccountAttemptRef = useRef(0)
  /**
   * **Both rails, and the row reads `present` rather than `available` (D266).** The mute silences
   * the hamburger dot and *keeps the row*, because the row is the container's only route to the
   * store — so the two questions are asked with two fields off one rail state: `AppBar` reads
   * `available`, this reads `present`.
   */
  const {
    present: updateAvailable,
    promptable: updatePromptable,
    promptableIfUrgent: updatePromptableIfUrgent,
    applying: updateApplying,
    labelKey: updateLabelKey,
    apply: applyUpdate,
  } = useAppUpdate()
  /**
   * **The install card is shown every time a signed-out visitor reaches the sign-in screen's
   * first step, not once per device (D160, amending D154).** D154's "once ever" read costed a
   * card against a resident's own history rather than against the actual moment it matters: a
   * device that was not installable (or was already dismissed) the first time a person landed
   * here stayed silent on every later visit too, including a second sign-in after a sign-out.
   *
   * *Where.* Unchanged from D154 — it used to sit on Home, above the gate list, on every launch.
   * On iOS that is the worst possible moment: a home-screen app gets its **own storage jar**,
   * separate from Safari's, so a resident who signs in and *then* installs lands in the installed
   * app signed out and pays another SMS to get back in. Asking before the phone number is entered
   * is the only placement where accepting costs nothing — install, then sign in once, inside the
   * app.
   *
   * *Every login.* `nudgeDismissed` no longer seeds from a persisted flag — it resets to `false`
   * the moment the route transitions into `auth` from anything else (the effect below), which
   * covers a fresh page load, a session expiring mid-use, and an explicit sign-out alike. A "Not
   * now" or an "Add" still hides the card for the rest of *this* visit to the screen, so it does
   * not fight the resident who just answered it.
   *
   * `canInstall` (not `!isStandalone`) is the gate, so nothing offers an install to a browser that
   * has no way to perform one; the hook keeps it live through `appinstalled` and a display-mode
   * listener, so an install mid-session takes the card and both dots with it at that moment.
   */
  const [nudgeDismissed, setNudgeDismissed] = useState(false)
  /**
   * **D282's offer, and since D286 it is answered ONCE PER LAUNCH like the update prompt below.**
   * D282 (f) reset it on every route transition into Home, on the reasoning that a resident who
   * declines a one-off migration this time may take it next time — which D286 answers with *not on
   * this screen*: Home is where a resident arrives to open a gate, and a modal that returns after
   * every trip to Gate Detail is an interruption they have already answered. So there is no
   * `route.id === 'home'` transition arm in the effect below, for either flag, and adding one back
   * is the edit to refuse.
   */
  const [betaDismissed, setBetaDismissed] = useState(false)
  /**
   * **D285 — the update prompt is answered ONCE PER LAUNCH, and this flag is deliberately never
   * reset.** D286 gave `betaDismissed` above the same rule, so the two launch prompts now differ in
   * *what* they offer and in nothing else; the argument is D285's own, read across both.
   *
   * **In memory, like `betaDismissed` and for `device-cache.md`'s reason**: a launch is exactly
   * what a fresh module scope measures, so a cold start asks again and nothing is persisted. A
   * resident who wants it to stop asking has the preference (`updateAlerts`), which is the answer
   * that lasts.
   */
  const [updatePromptDismissed, setUpdatePromptDismissed] = useState(false)
  /**
   * **D285 — whether D282's prompt has been raised at all this launch.** Both prompts mount on
   * Home at launch, so `!betaVisible` alone would merely queue this one *behind* the beta offer:
   * the resident taps *Not now* and a second modal takes its place in the same frame. D282's own
   * note weighs "two launch-time cards on one screen" and refuses it; two in a row is that cost
   * paid twice, so the update prompt yields for the whole launch instead.
   *
   * **The beta offer wins the tie deliberately.** It is the campaign to leave this shell
   * altogether (D270), and an update to a bundle we are asking the resident to abandon is the
   * lesser of the two — while the dot and the sidebar row stay behind it either way, which is what
   * makes yielding cost nothing.
   *
   * A ref rather than state: nothing renders from it, and the effect that sets it runs after the
   * paint in which `betaVisible` was true — which is necessarily before the tap that clears it, so
   * the value is already correct on the render that reads it. Writing `true` is idempotent, so
   * `StrictMode`'s double-invoked effect costs nothing.
   */
  const betaRaisedRef = useRef(false)
  const previousRouteId = useRef(route.id)
  const [languagePopoverOpen, setLanguagePopoverOpen] = useState(false)
  const [languageBusy, setLanguageBusy] = useState(false)
  const [languageError, setLanguageError] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  /**
   * D178 — the launch screen. Called here, above the boot gate, because it is a hook and the gate
   * is an early return; `BootSplash`'s own docblock carries what it waits for and what outranks
   * that. Rendered from a single mount point at the bottom of this component so the auth-loading
   * branch and the routed one hand it over without a remount the eye can catch.
   */
  //
  // A **restoring** boot is not resolved either: its read rejected and is being retried, and a
  // re-read that succeeds inside the splash's own ceiling should be invisible rather than a flash of
  // Home's loading line. Past the ceiling the splash yields to Home as it always does — never to
  // the OTP screen, which `requireAuth` refuses while restoring.
  const splashVisible = useBootSplashVisible(
    auth.state !== 'loading' && auth.state !== 'restoring',
    auth.state === 'signed-in',
  )

  /**
   * The first step only. `code` is a one-shot SMS with a resend timer running, and `name` is
   * D139's mandatory gate — the two moments in the app where an unsolicited card competing for
   * the same thumb is most expensive, and the second of which the resident cannot leave.
   */
  const nudgeVisible =
    !nudgeDismissed && canInstall && route.id === 'auth' && authScreenProps.step === 'phone'

  /**
   * **D282 — the move to the store-delivered app, offered once per launch on Home.**
   *
   * D270 de-prioritised the PWA and named its exit as a hand-off; this is the hand-off. On Android
   * the destination is `beta.selogate.com` — **D284** moved it off the marketing site to its own
   * origin and its own Cloudflare account, so a beta deploy token cannot reach the legal documents
   * — and it is a separately deployed page rather than a route here because it is read by someone
   * in the act of leaving this app and has to keep working after they have. **D313 gives iOS the
   * same offer and a different door**: Apple runs that beta itself, so the destination is the
   * TestFlight invite link and there is no page of ours in between. `androidBetaUrl()` and
   * `iosBetaUrl()` are the one place each host is named.
   *
   * **Five terms, and each of them is load-bearing.**
   *
   * `route.id === 'home'` — the launch destination, and the only route with no task in progress.
   * The popover renders as a sibling of `<Drawer>`, and *without* this term that mount point is
   * every signed-in route: a launch modal over Invite, over Install, over Gate Detail. This is the
   * "unsolicited card competing for the same thumb" the nudge's own note above weighs, answered by
   * placement rather than by copy.
   *
   * **Not the sign-in screen, despite D160's precedent.** That argument transfers — the container
   * is a separate storage jar, so migrating after signing in costs another SMS — but the *audience*
   * does not: this campaign's residents hold persisted sessions and rarely see auth at all, so
   * asking there would deliver almost no impressions, and it would put two launch-time cards on one
   * screen, `canInstall` being true on Android.
   *
   * `!splashVisible` — `BootSplash` is rendered last and covers the frame including the sheets, so
   * without this the popover mounts underneath it and `ConfirmPopover`'s mount-time focus lands on
   * a dialog nobody can see.
   *
   * `session.online` — the destination is cross-origin, so offline the one action this modal offers
   * goes nowhere. A modal whose only action is dead is worse than silence.
   *
   * `isBetaOfferPlatform(platform) && !isNativeContainer()` — **D313 widened the
   * first half and left the second exactly as D282 wrote it**, because the second term is what
   * carries both platforms. On Android the Capacitor WebView reports an Android user agent like any
   * other and `androidScheme: 'https'` makes its origin byte-identical to the web one; on iOS the
   * WKWebView says `iPhone` and `navigator.standalone` is `undefined` there (see
   * `pwa/nativeContainer.ts`). So on **either** platform `platform` alone offers a resident already
   * *in* the store app the move to it, and `App.test.tsx` carries a container case for each.
   *
   * **`platform === 'other'` is still excluded, and that is the term doing the work now**: a
   * desktop browser has no store build to move to, so the offer would point at nothing it can
   * install. Widening this to *every platform* is the edit to refuse.
   *
   * **D320 withdrew the Android arm as a LIST rather than a deleted term, and D338 put it back by
   * that list.** `BETA_OFFER_PLATFORMS` (`pwa/betaOffer.ts`) holds both platforms again; every
   * other term here is untouched by either change. That is why this reads
   * `isBetaOfferPlatform(platform)` rather than a platform comparison: one line withdraws or
   * restores an arm. The Android door is `androidBetaUrl()`'s `?flow=migrate` — the beta page's
   * flow for a resident who already has the product — while `website/invite.html` keeps the plain
   * flow for an invitee who has no app at all. **D349 took Android off the list again** until the
   * Play listing is public; the door and this gate are unchanged, so the list holds `'ios'` alone.
   *
   * **Deliberately not gated on `canInstall` or `isStandalone`.** Those ask whether this browser
   * can add a home-screen shortcut; a resident who already added one still has to migrate, and is
   * if anything the likeliest to want to.
   *
   * The flag is in memory and nothing is persisted — D160's own posture, and `device-cache.md`'s
   * rule that installation is browser/OS state rather than an SeloGate record. **Since D286 that
   * memory lasts the launch rather than the visit**: "Not now" holds until the next cold start, so
   * the offer is made once and the re-prompt schedule is the same one D285's prompt keeps. What
   * D282 (f) argued for the transition reset — a migration declined now may be taken later — is
   * answered by *not on this screen*, Home being where a resident arrives to open a gate.
   */
  const betaVisible =
    !betaDismissed &&
    route.id === 'home' &&
    !splashVisible &&
    session.online &&
    isBetaOfferPlatform(platform) &&
    !isNativeContainer()

  /**
   * **D313 — which door this resident is being offered, and it is read from the same `platform`
   * the gate above uses rather than from a second detection.** `platform === 'ios'` is
   * `useInstallPrompt`'s own answer, which also resolves an iPad reporting itself as Mac Safari
   * (`detectPlatform`'s touch check) — a device TestFlight serves exactly as it serves an iPhone.
   *
   * **Two destinations and two marks, and the pair moves together on purpose.** A title naming one
   * store while the tap opens the other is the failure `catalogs.test.ts` cannot see: both halves
   * typecheck, both render, and only a resident finds out. So the mark is derived from the same
   * expression as the URL, one line apart, rather than being chosen at either call site.
   */
  const betaIsIos = platform === 'ios'

  /**
   * **D285 — a newer version exists and the resident asked to be told about it here.**
   *
   * `updatePromptable` carries the rail state and the preference (`useAppUpdate`); the four terms
   * here are the placement, and three of them are D282's own, for D282's own reasons:
   *
   * `route.id === 'home'` — the mount point below is every signed-in route, so without it this is
   * a modal over Invite, over Install, over Gate Detail. `!splashVisible` — `BootSplash` covers the
   * frame including the sheets, so a prompt raised under it takes `ConfirmPopover`'s mount-time
   * focus to a dialog nobody can see. And the beta pair, which is the ordering above.
   *
   * **`session.online` is NOT a term here, and the difference from D282 is the action.** That
   * prompt's one control opens a cross-origin page, which offline goes nowhere; this one's applies
   * a shell the service worker already holds, or hands Play a download it will resume — neither of
   * which needs the network at the moment of the tap, and the PWA rail's apply is precisely the
   * update that arrived *before* the connection went.
   */
  /**
   * **D345 — `menu` prompts too, but only below the operator's soft floor.** The rail-and-preference
   * half is `useAppUpdate`'s `promptableIfUrgent`; the urgency half is read here and pushed in,
   * because `screens/home/` imports that hook and the open path may not reach `config/`. Either
   * answer then takes every placement term below unchanged — once per launch, on Home, behind the
   * splash, yielding to D282 — so an urgent prompt is D285's prompt exactly, raised for fewer updates.
   */
  const buildBelowPromptFloor = useBuildBelowPromptFloor()
  const updatePromptVisible =
    !updatePromptDismissed &&
    (updatePromptable || (updatePromptableIfUrgent && buildBelowPromptFloor)) &&
    route.id === 'home' &&
    !splashVisible &&
    !betaVisible &&
    !betaRaisedRef.current

  /**
   * **D321 — the one place the session's account identity reaches the config rail.**
   *
   * A cohorted flag's arm is `bucket < percent` over this identity, hashed **on the device**
   * (D321) — so the identity has to get from the session to `config/`, and the direction is not
   * a preference. Nothing under `auth/` may import `config/`: `screens/home/` imports `auth/`,
   * so an `auth/` → `config/` edge would put the open path one hop from the config module and
   * redden `infra/tests/client-config-client.test.ts`'s import-graph walk. The shell pushes it
   * in from here, which is where `useSession()` and `useFlag()` already both live.
   *
   * **`session.userId` and not a Cognito `sub`.** The client never receives one — the account
   * row is keyed by its own `randomUUID()` and carries `cognitoSub` as an attribute, so D49's
   * pool restore rewrites the `sub` and leaves `userId` standing. D321's row named the `sub`;
   * the correction is recorded there.
   *
   * **`undefined` while loading and after sign-out is the honest value, not a gap** — D325: a
   * client with no session is in no cohort and takes every rule's `off` arm. `setCohortIdentity`
   * is a no-op on an equal value, so this effect re-publishes only on a real account change.
   */
  useEffect(() => {
    setCohortIdentity(session.userId)
  }, [session.userId])

  /**
   * D285 — see `betaRaisedRef`. Keyed on `betaVisible` rather than on `betaDismissed` so it
   * records the offer having been *shown*, which is the thing the update prompt yields to.
   */
  useEffect(() => {
    if (betaVisible) betaRaisedRef.current = true
  }, [betaVisible])

  /**
   * D160 — resets the card for the *next* arrival at sign-in, not the current one: `nudgeVisible`
   * above already governs whether it shows on this render. Keyed on a route-id transition (not on
   * `route.id === 'auth'` alone) so it fires once per landing rather than on every re-render while
   * already on the screen, which would undo a same-visit "Not now"/"Add" dismissal.
   *
   * **D282's prompt used to reset here on the same terms, one route over, and D286 removed that
   * arm.** The two launch prompts now share one re-prompt schedule — once per launch, both of them
   * — so `betaDismissed` is answered exactly like `updatePromptDismissed` above it and this effect
   * is the sign-in card's alone. **The reset that went was never the guard against a same-visit
   * undo**: `betaVisible` reads the flag directly, so what the transition test bought was the
   * *return* on a second arrival at Home, which is the behaviour D286 withdraws. The auth arm keeps
   * the transition test for the reason it always had — `route.id === 'auth'` alone is true on every
   * re-render while the screen is showing, which would undo a same-visit dismissal.
   */
  useEffect(() => {
    if (route.id === 'auth' && previousRouteId.current !== 'auth') setNudgeDismissed(false)
    previousRouteId.current = route.id
  }, [route.id])

  /**
   * D145 — Android's hardware back closes the topmost of these instead of leaving the screen.
   *
   * **The self-name popover is no longer one of them (D188).** It used to be registered here as a
   * genuine pair with the drawer, because the edit pencil did not close the drawer and the popover
   * opened on top of it — which is the whole reason `.popover-backdrop` outranks `.drawer` in
   * `global.css`. The rename now lives on the Preferences screen, which is a route rather than a
   * layer, so the screen registers its own entry (`preferences:edit-name`) and the pairing that
   * needed explaining is gone. The deletion dialog below is now the only surviving pair.
   *
   * `NudgeSheet` is deliberately absent, and moving it to the sign-in screen strengthens rather
   * than weakens the reason: it is still the only layer here the resident did not open, and it now
   * sits on the one screen where back leads out of the app entirely. The iOS instructions sheet
   * *is* included — it carries no backdrop, but the resident chose to open it (from the sidebar
   * row, or from that card), and back is the gesture that undoes that.
   *
   * **`shell:update` is gone with the sheet it registered (D155).** The Update row applies the
   * waiting shell on the tap, so there is no layer left to dismiss — and the reload takes the whole
   * document with it, which no back entry could have survived anyway.
   */
  useDismissOnBack('shell:drawer', drawerOpen, () => setDrawerOpen(false))
  useDismissOnBack('shell:ios-install', iosSheetOpen, () => setIosSheetOpen(false))
  /**
   * P19 §3's deletion dialog, registered as a **pair with the drawer**, the way the rename
   * popover was before D188 moved it: the row leaves the sidebar open and the dialog opens on top of it, so
   * one back closes the dialog and the next closes the drawer.
   *
   * `dismissible: !deleteAccountInFlight` mirrors the backdrop the dialog itself refuses while the
   * `POST` is running — the two guards have to agree, for `GateDetailScreen`'s reason: a back tap
   * that dismissed what the backdrop would not would leave a cancelled-looking screen over an
   * irreversible request that is still going.
   */
  useDismissOnBack('shell:delete-account', deletingAccount, () => setDeletingAccount(false), {
    dismissible: !deleteAccountInFlight,
  })
  /**
   * The sign-out confirmation, registered as a **pair with the drawer** exactly like the deletion
   * dialog above: the row leaves the sidebar open and the dialog opens on top of it, so one back
   * closes the confirmation and the next closes the drawer.
   */
  useDismissOnBack('shell:sign-out', signingOut, () => setSigningOut(false))

  /**
   * The drawer's language row opens `LanguagePopover`; picking an option here is the **only**
   * thing that changes `locale`, and a cloud write.
   *
   * `locale` is a cloud-owned setting (`device-cache.md`'s table), and `AuthProvider` reconciles
   * it from the cached profile on every boot because *"a server change wins"*. So a toggle that
   * only moved React state was not just unsaved — the next launch actively reverted it, silently.
   * The `PATCH` is what makes the choice real; the two lines after it are what makes it visible
   * and durable, and **neither is redundant**:
   *
   *  - `rememberProfile` writes the account's new locale to the cached profile, which is what a
   *    returning launch renders **before** the network answers. Under D139 the boot refresh also
   *    returns the profile and wins over this row, so the write is no longer the only thing
   *    standing between the choice and a silent revert — but it is still what makes the first
   *    paint of the next launch correct, and it is the whole of it while offline.
   *  - `setLocale` moves the UI. `AuthProvider`'s reconcile effect would also fire off the
   *    session state `rememberProfile` just changed, but leaving the control's own effect to a
   *    listener in another file is how it stops working without anyone touching this line.
   *
   * It is **pessimistic**, matching the Preferences screen's rename: nothing on screen moves until the server
   * has accepted the value, and the value adopted is the one the server **echoed**. A failure
   * therefore cannot leave the app rendering a language the account does not have — there is no
   * optimistic flip to roll back, and the popover stays open with the error rather than closing on
   * a write that never landed.
   */
  async function selectLanguage(next: Locale): Promise<void> {
    if (next === locale) {
      setLanguagePopoverOpen(false)
      return
    }
    if (languageBusy || !session.online) return
    setLanguageBusy(true)
    setLanguageError(false)
    try {
      const result = await patchProfile(session.request, { locale: next })
      if (!result.ok) {
        setLanguageError(true)
        return
      }
      await session.rememberProfile(result.data)
      setLocale(result.data.locale)
      setLanguagePopoverOpen(false)
    } catch (caught) {
      if (!(caught instanceof NetworkError)) throw caught
      setLanguageError(true)
    } finally {
      setLanguageBusy(false)
    }
  }

  /**
   * P19 §3 — the account's own erasure. **The last thing this session ever does.**
   *
   * `202` is the success and there is nothing further to wait for: the destructive pass finished
   * inside the request and the log sweeps cannot (`packages/functions/privacy/routes.ts`). So the
   * only correct next step is the local teardown, and it goes through `signOut()` rather than a
   * hand-rolled purge — `SessionManager.endSession()` is the one place every session ending lands,
   * *"so none of them can be the one that forgets the snapshot"*, and a second teardown here would
   * be exactly that. Its own revoke call failing against an account that no longer exists is
   * already swallowed there; an offline sign-out is still a sign-out.
   *
   * **A `404` is a success too.** It means a session whose profile was already gone — there is
   * nothing left to erase, and offering a retry for work that can never succeed would strand the
   * one person entitled to leave. Every other status is an error line and the dialog stays open.
   *
   * **The typed confirmation is the account's own phone number and only the server can settle
   * it** (`packages/functions/privacy/erase.ts`): the client holds no copy, so a `403
   * phone-mismatch` is the answer to a mistyped or borrowed number, and it is a line under the
   * slider rather than a teardown.
   *
   * `gatesWithoutManager` is deliberately **not rendered**: nothing survives the teardown to render
   * it on, and the warning that matters is the standing one in the dialog *before* the tap. Filed
   * as a named gap in `implementation-notes.md` → P19 rather than dropped quietly.
   */
  async function confirmDeleteAccount(confirmPhone: string): Promise<void> {
    if (deleteAccountRequestRef.current) return
    deleteAccountRequestRef.current = true
    const attempt = (deleteAccountAttemptRef.current += 1)
    const current = (): boolean => deleteAccountAttemptRef.current === attempt
    setDeleteAccountInFlight(true)
    setDeleteAccountError(undefined)
    try {
      const result = await eraseMyAccount(session.request, confirmPhone)
      if (!current()) return
      if (!result.ok && result.status !== 404) {
        // A `403 phone-mismatch` is the typed number not being this account's — the one error
        // here the resident can act on, and the only reason the dialog stays open worth naming
        // separately. Every other status is the generic retry line.
        setDeleteAccountError(result.code === 'phone-mismatch' ? 'mismatch' : 'server')
        return
      }
      // The dialog goes with the session. Left mounted, it would sit over the sign-in screen with
      // a typed confirmation still in it.
      setDeletingAccount(false)
      setDrawerOpen(false)
      signOut()
    } catch (caught) {
      if (!(caught instanceof NetworkError)) throw caught
      if (!current()) return
      setDeleteAccountError('network')
    } finally {
      /**
       * **Every one of this dialog's exits is gated on `deleteAccountInFlight`** — the backdrop
       * (`onClick={busy ? undefined : onCancel}`), the Cancel button, and `useDismissOnBack`'s
       * `dismissible` — so a flag left set is not a cosmetic defect: it is an app that needs
       * restarting, on the screen where being stuck matters most.
       *
       * Which is why it releases in a `finally` rather than on each branch. Two paths do not
       * reach a branch: the rethrow above (anything that is not a `NetworkError` leaves this
       * function without ever answering the screen), and the success path, which used to leave
       * the flag set for the life of the page — `signOut()` does not reload, `App` stays mounted,
       * and a second sign-in on the same page load then opened this dialog *already busy and
       * undismissable*.
       *
       * A superseded attempt clears nothing: the attempt that supersedes it owns the flag now.
       */
      if (current()) {
        deleteAccountRequestRef.current = false
        setDeleteAccountInFlight(false)
      }
    }
  }

  /**
   * The one door into the dialog — and it starts every attempt from rest.
   *
   * Clearing the error was always here; what is new is that the in-flight pair is cleared too, and
   * that opening **invalidates any attempt still running**. Both are belt: with the `finally`
   * above, no path should reach this holding a set flag. Belt is the point — the failure it
   * guards against is a dialog nobody can close, and the cost of it is two assignments.
   */
  function openDeleteAccount(): void {
    deleteAccountAttemptRef.current += 1
    deleteAccountRequestRef.current = false
    setDeleteAccountInFlight(false)
    setDeleteAccountError(undefined)
    setDeletingAccount(true)
  }

  /**
   * **The drawer stays open, and closing it was the bug.**
   *
   * The symptom on iPhone was that tapping the sidebar's Add-to-Home-Screen row did nothing but
   * shut the sidebar — no instructions, and a back tap afterwards that went nowhere. The cause is
   * D145: closing an overlay routes through `history.go(-1)`, which lands a tick later, while
   * opening the sheet pushes its entry **now**. So the drawer's own unwind arrived after the
   * push and popped the sheet's entry instead of its own, and `useDismissOnBack` dutifully
   * reported the sheet closed the same frame it opened. Nothing logged; the sheet was never on
   * screen long enough to see.
   *
   * The close was already redundant when it broke: the drawer painting over the sheet was fixed
   * in `global.css` by giving `.install-sheet` a `z-index` of 40 against `.drawer`'s 35, and its
   * comment says so. So the sheet now opens **on top of the open drawer**, exactly as the rename
   * popover does, and back closes them one at a time in the order they were opened.
   */
  async function handleAddToHomeScreen(): Promise<void> {
    if (platform === 'ios') {
      setIosSheetOpen(true)
      return
    }
    if (canPromptNatively) await promptInstall()
  }

  if (auth.state === 'loading') {
    return (
      <PhoneFrame>
        {/* An empty scroller, and deliberately not a message. The word "טוען…" used to sit here;
            D178 replaced it with the mark above, which says the same thing with the app's own
            face and — unlike the text — keeps saying it while the gate list arrives. The node is
            not registered with `setShellScroller`: there is nothing on it to scroll to. */}
        <div className="app-screen no-bar" />
        <BootSplash visible={splashVisible} />
      </PhoneFrame>
    )
  }

  /**
   * P7's eight routes and the shell's own `about` render their own `AppBar` (a hamburger bar
   * for `home`, a back bar for everything else); `RoutePlaceholder` does the same (a back bar) for
   * the ids P9/P15 haven't landed screens for yet (`schedule`'s tile lives inside `gate`; `device`
   * still falls through to it, and `devices`/`copyMembers` are P15's own screens, each added with
   * the entry point that opens it — see R1-10 in
   * `R1-client-resilience.md`). **`activity` is P9's own screen, added with this PRD.**
   * `activityRow` stays a placeholder: the row detail is an in-page sheet on `activity`
   * (mirroring `PeopleScreen`'s member sheet), not a route this shell navigates to. `shabbat`
   * has a screen but stays behind the shabbat_panel remote flag (`config/`, D329) — off, it
   * falls through the same way.
   */
  function renderScreen(): JSX.Element {
    switch (route.id) {
      case 'home':
        return <HomeScreen onMenu={() => setDrawerOpen(true)} menuButtonRef={menuButtonRef} />
      case 'gate':
        return <GateDetailScreen gateId={route.gateId} />
      case 'personalize':
        return <PersonalizeScreen gateId={route.gateId} />
      case 'people':
        return <PeopleScreen gateId={route.gateId} />
      // D265 — Access management, split out of `gate`'s own manager-only section. Reached from
      // Gate Detail's one `ניהול גישה ›` row and from nowhere else: no drawer entry, and no
      // resident path, the row that opens it being manager-only exactly as the section was.
      case 'access':
        return <AccessScreen gateId={route.gateId} />
      case 'invite':
        return <InviteScreen gateId={route.gateId} />
      case 'activity':
        return <ActivityScreen gateId={route.gateId} />
      case 'install':
        return <InstallScreen />
      // D267 — the same screen, entered on D176's update branch for a gate that already exists.
      // Reached from the Access page's `שינויי התקנה ›` row; a typed URL is the only other way in,
      // and the menu refuses a confirmed resident there.
      case 'installEdit':
        return <InstallScreen updateFor={route.gateId} />
      case 'about':
        return <AboutScreen />
      case 'preferences':
        return (
          <PreferencesScreen onDeleteAccount={openDeleteAccount} deleteAccountDisabled={!session.online} />
        )
      case 'devices':
        return <DevicesScreen />
      case 'copyMembers':
        return <CopyMembersScreen gateId={route.gateId} direction={route.direction} />
      case 'shabbat':
        // shabbat_panel (D329) — R9's first remote flag, a migration of the panel's own
        // compile-time kill switch with no product change. Off, this falls through exactly like
        // it did before the panel existed: `RoutePlaceholder` for a route with no screen.
        return shabbatPanel ? (
          <ShabbatScreen gateId={route.gateId} />
        ) : (
          <RoutePlaceholder routeId={route.id} />
        )
      default:
        return <RoutePlaceholder routeId={route.id} />
    }
  }

  return (
    <PhoneFrame>
      {route.id === 'auth' ? (
        <>
          <div className="app-screen no-bar">
            <AuthScreen {...authScreenProps} />
          </div>
          {/* No `menuButtonRef`: this screen has no app bar, so there is nothing to fly into.
              See `NudgeSheet`'s docblock. */}
          <NudgeSheet
            visible={nudgeVisible}
            onAdd={() => {
              void handleAddToHomeScreen()
            }}
            onDismiss={() => setNudgeDismissed(true)}
          />
        </>
      ) : (
        <>
          {/* R1-5: the routed content, and only it. The bar, drawer and sheets stay outside, so a
              screen that throws leaves the user the chrome they navigate with — and `resetKey`
              means walking away from the broken route is itself the recovery. */}
          {/* `ref` registers the product's one scroll container (D132 left exactly one) with
              `shellScroller`, which is how the app-bar title and the iOS status-bar bridge reach
              it from outside any screen's subtree. React clears it on unmount, so nothing can
              scroll a detached node. */}
          <div className="app-screen" ref={setShellScroller}>
            <ErrorBoundary resetKey={route.id}>{renderScreen()}</ErrorBoundary>
          </div>
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            {...(session.name === undefined ? {} : { name: session.name })}
            /* Same shape as Install gate, Devices and About. It carries no disabled state: half
               of what the screen holds is device-local and works offline, and the rename inside it
               refuses on its own row. */
            onPreferences={() => {
              setDrawerOpen(false)
              navigate({ id: 'preferences' })
            }}
            onAddToHomeScreen={
              canInstall
                ? () => {
                    void handleAddToHomeScreen()
                  }
                : null
            }
            /* The sidebar half of the pair the card is shown once instead of repeatedly. Same
               condition as the row itself, because the row exists only while there is an install
               to offer — so the dot is not a second state to keep in step, it is the row saying
               what it is for. Both go the moment the app is installed. */
            addToHomeScreenDot={canInstall}
            onInstallGate={() => {
              setDrawerOpen(false)
              navigate({ id: 'install' })
            }}
            /* Same shape as Install gate and About: close the drawer, then navigate. `commit()`
               unwinds the drawer's own history entry first (D145), so the two are one back tap
               apart rather than two. */
            onDevices={() => {
              setDrawerOpen(false)
              navigate({ id: 'devices' })
            }}
            onOpenLanguage={() => {
              setLanguageError(false)
              setLanguagePopoverOpen(true)
            }}
            languageDisabled={!session.online || languageBusy}
            onAbout={() => {
              setDrawerOpen(false)
              navigate({ id: 'about' })
            }}
            /* D155: the tap **is** the update. The row exists only when there is something to
               apply, so there is nothing for a confirmation step to add — the resident answered
               the question by tapping a row that says what it does. The drawer is left open
               deliberately: on the PWA rail the reload closes it, and closing it first would take
               the row's own applying state off screen before the page goes.

               **D137 is unamended in the part that matters, and D266 strengthens it.** `apply()`
               is now the only caller of `applyShellUpdate()` *and* of `applyStoreUpdate()`, and
               the app still never applies an update on its own — on Android the row's second tap
               is a second consent, reading *Restart to finish* rather than *Update now*, where
               this rail spends one tap on both `SKIP_WAITING` and a reload. */
            onUpdate={applyUpdate}
            updateAvailable={updateAvailable}
            updateApplying={updateApplying}
            updateLabelKey={updateLabelKey}
            /* The drawer stays open — the confirmation opens on top of it, the same shape as the
               deletion dialog below. */
            onSignOut={() => setSigningOut(true)}
          />
          {signingOut ? (
            <ConfirmPopover
              title={tDrawer('signOutConfirmTitle')}
              body={tDrawer('signOutConfirmBody')}
              cancelLabel={tCommon('cancel')}
              confirmLabel={tDrawer('signOut')}
              onCancel={() => setSigningOut(false)}
              onConfirm={() => {
                setSigningOut(false)
                setDrawerOpen(false)
                signOut()
              }}
            />
          ) : null}
          {/* A sibling of `<Drawer>`, not a child — see `Popover`'s note on stacking contexts. */}
          {languagePopoverOpen ? (
            <LanguagePopover
              title={tDrawer('language')}
              current={locale}
              busy={languageBusy}
              {...(languageError ? { error: tDrawer('languageErrorSave') } : {})}
              onSelect={(next) => void selectLanguage(next)}
              onCancel={() => setLanguagePopoverOpen(false)}
            />
          ) : null}
          {deletingAccount ? (
            <DeleteAccountDialog
              busy={deleteAccountInFlight}
              {...(deleteAccountError === undefined
                ? {}
                : {
                    error: tAccount(
                      deleteAccountError === 'network'
                        ? 'deleteFailedNetwork'
                        : deleteAccountError === 'mismatch'
                          ? 'deleteMismatch'
                          : 'deleteFailed',
                    ),
                  })}
              onCancel={() => setDeletingAccount(false)}
              /* The sentence under the slider is the server's answer about a number that is no
                 longer on screen — see the dialog's own `onEdit` note. */
              onEdit={() => setDeleteAccountError(undefined)}
              onConfirm={(phone) => void confirmDeleteAccount(phone)}
            />
          ) : null}
          {/*
            D282 — the Android move, a sibling of `<Drawer>` for `Popover`'s stacking-context
            reason and gated on Home by `betaVisible` above.

            **`destructive: false`.** The prop names the tone, never the shape: `ConfirmPopover`'s
            default paints the confirm as a danger action, and leaving for the store is the thing
            this page wants tapped.

            **The mark is a literal here rather than a catalog string**, and that is D259's own
            mechanism rather than a workaround: `catalogs.test.ts` refuses `/google/i` in any
            catalog because a brand name that reaches a translator is a brand name that gets
            translated, and Google's guidelines say this one must not be. So the copy carries
            `{{store}}` in all five locales and the untranslatable half is supplied from code.
            **D313 makes that literal a pair** — `TestFlight` on iOS, `Google Play` on Android —
            which is the same mechanism serving a second mark rather than a second mechanism; the
            body says *in the store* in all five locales for the same reason, since the one sentence
            has to be true of both doors.

            **No `useDismissOnBack`.** D145 excludes `NudgeSheet` as the one layer the resident did
            not open, and D250's sharper form of that rule decides this one: the popover is raised
            at launch with **no gesture behind it**, and Chrome marks a gesture-less `pushState`
            skippable — so back would leave the screen anyway and then fire `onClose` on a document
            already navigating, which is an entry that lies about what it owns. Unlike D250's
            address book there is no later gesture to arm it on, every tap in here being a
            dismissal. 🔸 The cost, so that nobody "fixes" it: on Android, hardware back with this
            open leaves the app. It is bounded — the backdrop, "Not now" and Escape are three
            dismissals, and the offer returns on the next launch, so leaving loses nothing.

            The link opens in a new tab rather than navigating this one: the destination is
            cross-origin and outside the manifest's `scope`, so an installed shell hands it to the
            platform's own browser and the resident's way back is the platform's, not a link we
            would have to put on somebody else's page.
          */}
          {betaVisible ? (
            <ConfirmPopover
              title={tAndroidBeta('title', { store: betaIsIos ? 'TestFlight' : 'Google Play' })}
              body={tAndroidBeta('body')}
              cancelLabel={tAndroidBeta('notNow')}
              confirmLabel={tAndroidBeta('show')}
              destructive={false}
              onCancel={() => setBetaDismissed(true)}
              onConfirm={() => {
                setBetaDismissed(true)
                window.open(
                  betaIsIos ? iosBetaUrl() : androidBetaUrl(locale),
                  '_blank',
                  'noopener,noreferrer',
                )
              }}
            />
          ) : null}
          {/*
            D285 — the new version, offered once per launch on Home. A sibling of `<Drawer>` for
            `Popover`'s stacking-context reason, and gated by `updatePromptVisible` above.

            **The copy is `drawer`'s, and the confirm is the sidebar row's own `labelKey`.** D275
            established the rule when Home's banner reused `updateRestart`: one tap named twice is
            how two surfaces come to describe it differently at the next retranslation. So this
            reads *Update now* / *Restart to finish* / *Update in App Store* exactly as the row
            does, which also means the prompt cannot claim an action the rail is not offering.
            `catalogs.test.ts` already holds the title apart from all four labels for the banner;
            the same case now covers this title.

            **`destructive: false`.** The prop names the tone, never the shape (D282): applying an
            update is the thing this dialog wants tapped.

            **No `useDismissOnBack`, on D282's reasoning verbatim** — the popover is raised at
            launch with no gesture behind it, and Chrome marks a gesture-less `pushState`
            skippable, so back would leave the screen and then fire `onClose` on a document already
            navigating. 🔸 The cost is the same and is bounded the same way: on Android, hardware
            back with this open leaves the app; the backdrop, *Not now* and Escape are three
            dismissals, and the dot and the sidebar row are still there afterwards.

            **Both answers dismiss for the launch**, including the confirm — on the PWA rail the
            apply reloads the document, which resets the flag by construction; on the store rail it
            does not, and a prompt still standing over a download it just started would be offering
            to start it again.
          */}
          {updatePromptVisible ? (
            <ConfirmPopover
              title={tDrawer('updatePromptTitle')}
              body={tDrawer('updatePromptBody')}
              cancelLabel={tDrawer('updateNotNow')}
              confirmLabel={tDrawer(updateLabelKey)}
              destructive={false}
              onCancel={() => setUpdatePromptDismissed(true)}
              onConfirm={() => {
                setUpdatePromptDismissed(true)
                applyUpdate()
              }}
            />
          ) : null}
        </>
      )}
      {/* Outside the branch: the iOS instructions are opened from the card on the sign-in screen
       *and* from the sidebar row afterwards, and it is the same sheet either way. */}
      <InstallPromptSheet open={iosSheetOpen} onClose={() => setIosSheetOpen(false)} />
      {/* Last, so it covers the frame including the sheets, and outside the branch so the crossing
          from the auth-loading gate above lands on a mount point that does not move. */}
      <BootSplash visible={splashVisible} />
    </PhoneFrame>
  )
}
